import { Auth as AmplifyAuth } from '@aws-amplify/auth';
import { isMFAEnabled } from '../../utils/mfa';
import { clearDeviceTrust, markDeviceTrusted } from '../../utils/mfaDeviceTrust';

// SRP is required for Cognito device-remembering (the "remember this device"
// MFA feature). Driven by env so it can be enabled per environment as the
// matching User Pool / app client changes are rolled out. Defaults to the
// legacy password flow so environments without the AWS changes keep working.
AmplifyAuth.configure({
  region: process.env.REACT_APP_COGNITO_REGION,
  userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  userPoolWebClientId: process.env.REACT_APP_COGNITO_WEB_CLIENT_ID,
  authenticationFlowType:
    process.env.REACT_APP_COGNITO_AUTH_FLOW || 'USER_PASSWORD_AUTH',
});

export { AmplifyAuth };

// Marks the current browser as a Cognito "remembered device" so future logins
// can skip the MFA challenge, and records when it was trusted (for the 30-day
// expiry enforced in the sign-in flow).
export async function rememberCurrentDevice() {
  await AmplifyAuth.rememberDevice();
  markDeviceTrusted();
}

// Drops device trust both in Cognito and locally. Safe to call even when the
// device was never remembered (errors are swallowed) so it doubles as cleanup.
export async function forgetCurrentDevice() {
  try {
    await AmplifyAuth.forgetDevice();
  } catch (e) {
    // device may not be tracked/remembered yet - nothing to forget
  }
  clearDeviceTrust();
}

export function getAmplifyAuthData(result = {}) {
  const session = result.getSignInUserSession();
  const { preferredMFA } = result;
  const { accessToken, idToken, refreshToken } = session;
  const { email, family_name: familyName, name } = idToken.payload;
  const { exp: accessExpires, auth_time: authTime, username } = accessToken.payload;
  const mfaEnabled = isMFAEnabled(preferredMFA);

  return {
    accessToken: accessToken.jwtToken,
    refreshToken: refreshToken.token,
    accessExpires,
    authTime,
    mfaEnabled,
    preferredMFA,
    username,
    email,
    name,
    familyName,
  };
}
